import React, { useState, useRef, useCallback } from 'react';
import type { IngredientCatalog } from '../models/diary';
import { ingredientService } from '../services/ingredientService';
import SearchableSelect from './SearchableSelect';

interface IngredientSelectProps {
  value: number | '';
  // The currently selected ingredient. Injected into the option list so the
  // selection renders even when it isn't part of the loaded/searched page.
  selected?: IngredientCatalog;
  onChange: (ingredient: IngredientCatalog) => void;
  hasError?: boolean;
}

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

// Async ingredient picker: lazy-loads the catalog on first open, performs
// server-side search via the `search` query param, and paginates with infinite
// scroll. Each instance owns its own list so searches stay independent per row.
const IngredientSelect: React.FC<IngredientSelectProps> = ({ value, selected, onChange, hasError }) => {
  const [items, setItems] = useState<IngredientCatalog[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // The term currently in flight, used to guard against stale responses and to
  // know which query to paginate.
  const termRef = useRef('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const load = useCallback(async (pageToLoad: number, term: string, append: boolean) => {
    setLoading(true);
    try {
      const res = await ingredientService.getAll(pageToLoad, PER_PAGE, term);
      if (termRef.current !== term) return; // a newer search superseded this one
      setItems(prev => (append ? [...prev, ...res.results] : res.results));
      setHasMore(pageToLoad < res.totalPages);
      setPage(pageToLoad);
    } catch (e) {
      console.error('Error loading ingredients:', e);
    } finally {
      if (termRef.current === term) setLoading(false);
    }
  }, []);

  const handleOpen = () => {
    if (!loaded) {
      setLoaded(true);
      termRef.current = '';
      load(1, '', false);
    }
  };

  const handleSearchChange = (term: string) => {
    termRef.current = term;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(1, term, false), SEARCH_DEBOUNCE_MS);
  };

  const handleScrollEnd = () => {
    if (!loading && hasMore) {
      load(page + 1, termRef.current, true);
    }
  };

  // Ensure the selected ingredient is always available for display/selection.
  const merged = selected && !items.some(i => i.id === selected.id)
    ? [selected, ...items]
    : items;

  const options = merged.map(c => ({
    value: c.id,
    label: `${c.name} (${c.calories} kcal/100g)`,
    searchText: c.name,
  }));

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={(v) => {
        const ing = merged.find(i => i.id === Number(v));
        if (ing) onChange(ing);
      }}
      placeholder="-- Selecciona ingrediente --"
      hasError={hasError}
      onOpen={handleOpen}
      onSearchChange={handleSearchChange}
      onScrollEnd={handleScrollEnd}
      isLoading={loading}
    />
  );
};

export default IngredientSelect;
